import { FileObject } from '@kalos/kalos-rpc';

import { convertHeicToJpeg, getEnhancedMimeType } from './helpers';

// ---------------------------------------------------------------------------
// Upload constraints
// ---------------------------------------------------------------------------

export const MAX_FILES = 10;
export const MAX_FILE_SIZE_BYTES = 150 * 1024 * 1024; // 150 MB
export const MAX_TOTAL_SIZE_BYTES = 200 * 1024 * 1024; // 200 MB

// ---------------------------------------------------------------------------
// Allowed MIME types
// ---------------------------------------------------------------------------

/** Full set of allowed types for note/memo uploads (images + videos + PDF). */
export const VALID_FILE_TYPES: string[] = [
  'image/jpeg',
  'image/jpg',
  'image/png',
  'image/webp',
  'image/heic',
  'image/heif',
  'video/mp4',
  'video/webm',
  'video/quicktime', // .mov
  'application/pdf',
];

/** Image-only subset (includes PDF) – used by photo upload forms. */
export const VALID_IMAGE_TYPES: string[] = [
  'image/jpeg',
  'image/jpg',
  'image/png',
  'image/webp',
  'image/heic',
  'image/heif',
  'application/pdf',
];

// ---------------------------------------------------------------------------
// FileUploader `accept` prop presets
// ---------------------------------------------------------------------------

/** Accept map for full file uploads (images + videos + PDF). */
export const FILE_UPLOAD_ACCEPT: Record<string, string[]> = {
  'image/jpeg': ['.jpg', '.jpeg'],
  'image/png': ['.png'],
  'image/heic': ['.heic'],
  'image/heif': ['.heif'],
  'image/webp': ['.webp'],
  'video/mp4': ['.mp4'],
  'video/webm': ['.webm'],
  'video/quicktime': ['.mov'],
  'application/pdf': ['.pdf'],
};

/** Accept map for image + PDF uploads only (no video). */
export const IMAGE_UPLOAD_ACCEPT: Record<string, string[]> = {
  'image/jpeg': ['.jpg', '.jpeg'],
  'image/png': ['.png'],
  'image/webp': ['.webp'],
  'image/heic': ['.heic'],
  'image/heif': ['.heif'],
  'application/pdf': ['.pdf'],
};

/** Accept map for image-only uploads (no PDF, no video). */
export const IMAGE_ONLY_ACCEPT: Record<string, string[]> = {
  'image/jpeg': ['.jpg', '.jpeg'],
  'image/png': ['.png'],
  'image/heic': ['.heic'],
  'image/heif': ['.heif'],
  'image/webp': ['.webp'],
};

// ---------------------------------------------------------------------------
// `accept` attribute strings for capture inputs
// ---------------------------------------------------------------------------

/** Flattens an accept map into the comma-separated form a file input expects. */
const acceptAttribute = (accept: Record<string, string[]>) =>
  Object.entries(accept)
    .flatMap(([mimeType, extensions]) => [mimeType, ...extensions])
    .join(',');

/** Photo capture: images only — no video, no PDF. */
export const PHOTO_CAPTURE_ACCEPT = acceptAttribute(IMAGE_ONLY_ACCEPT);

/** Images + PDF, no video. */
export const IMAGE_PDF_UPLOAD_ACCEPT = acceptAttribute(IMAGE_UPLOAD_ACCEPT);

/** Video capture: the video subset of the note upload set. */
export const VIDEO_CAPTURE_ACCEPT = acceptAttribute(
  Object.fromEntries(
    Object.entries(FILE_UPLOAD_ACCEPT).filter(([mimeType]) => mimeType.startsWith('video/')),
  ),
);

/** One tile for both: images and video, no PDF. */
export const MEDIA_CAPTURE_ACCEPT = `${PHOTO_CAPTURE_ACCEPT},${VIDEO_CAPTURE_ACCEPT}`;

// ---------------------------------------------------------------------------
// File validation helpers
// ---------------------------------------------------------------------------

/**
 * Validate a single file against a list of allowed MIME types using enhanced
 * (magic-byte) detection.
 */
export const isValidFileType = async (
  file: File,
  allowedTypes: string[] = VALID_FILE_TYPES,
): Promise<boolean> => {
  try {
    const arrayBuffer = await file.arrayBuffer();
    const fileData = new Uint8Array(arrayBuffer);
    const enhancedMime = getEnhancedMimeType(file.name, fileData);
    return allowedTypes.includes(enhancedMime);
  } catch (error) {
    console.error('Error validating file type:', error);
    return false;
  }
};

/**
 * Validate an array of files, returning the first invalid file (if any).
 */
export const validateAllFileTypes = async (
  files: File[],
  allowedTypes: string[] = VALID_FILE_TYPES,
): Promise<{ isValid: boolean; invalidFile?: File }> => {
  if (!files || files.length === 0) return { isValid: true };

  const checks = await Promise.all(files.map((f) => isValidFileType(f, allowedTypes)));
  const invalidIdx = checks.findIndex((v) => !v);

  if (invalidIdx !== -1) return { isValid: false, invalidFile: files[invalidIdx] };
  return { isValid: true };
};

// ---------------------------------------------------------------------------
// File-size validation (synchronous)
// ---------------------------------------------------------------------------

export interface FileSizeValidationResult {
  isValid: boolean;
  error?: string;
}

/**
 * Run all synchronous size / count validations on a file list.
 * Returns the first error encountered, or `{ isValid: true }`.
 */
export const validateFileSizes = (
  files: File[],
  {
    maxFiles = MAX_FILES,
    maxFileSize = MAX_FILE_SIZE_BYTES,
    maxTotalSize = MAX_TOTAL_SIZE_BYTES,
  }: { maxFiles?: number; maxFileSize?: number; maxTotalSize?: number } = {},
): FileSizeValidationResult => {
  if (files.length > maxFiles) {
    return { isValid: false, error: `Cannot upload more than ${maxFiles} files.` };
  }

  const oversize = files.find((f) => f.size > maxFileSize);
  if (oversize) {
    return {
      isValid: false,
      error: `File ${oversize.name} exceeds the ${Math.round(maxFileSize / 1024 / 1024)} MB limit.`,
    };
  }

  const totalSize = files.reduce((sum, f) => sum + f.size, 0);
  if (totalSize > maxTotalSize) {
    return {
      isValid: false,
      error: `Total size exceeds ${Math.round(maxTotalSize / 1024 / 1024)} MB (currently ${(totalSize / 1024 / 1024).toFixed(1)} MB).`,
    };
  }

  return { isValid: true };
};

// ---------------------------------------------------------------------------
// File naming
// ---------------------------------------------------------------------------

/** Generate a timestamped unique file name preserving the original extension. */
export const generateUniqueFileName = (originalName: string): string => {
  const ts = Date.now();
  const extension = originalName.split('.').pop();
  const baseName = originalName.replace(/\.[^/.]+$/, '');
  return `${baseName}-${ts}.${extension}`;
};

// ---------------------------------------------------------------------------
// Memo staging: downscale raster images before CreateAndUploadMemo
// (aligns with server compress max edge ~1200; cuts cellular upload size.)
// ---------------------------------------------------------------------------

export const MEMO_STAGING_MAX_EDGE_PX = 1200;
/** If dimensions already fit and blob is under this size, skip client re-encode (fewer lossy passes). */
export const MEMO_STAGING_SKIP_REENCODE_MAX_BYTES = 512 * 1024;
const MEMO_STAGING_JPEG_QUALITY = 0.82;
const MEMO_STAGING_WEBP_QUALITY = 0.85;

function scaledDimensions(
  width: number,
  height: number,
  maxEdge: number,
): { width: number; height: number } {
  if (width <= maxEdge && height <= maxEdge) {
    return { width, height };
  }
  const scale = Math.min(maxEdge / width, maxEdge / height);
  return {
    width: Math.max(1, Math.round(width * scale)),
    height: Math.max(1, Math.round(height * scale)),
  };
}

/**
 * Draw `bitmap` into a canvas at most `MEMO_STAGING_MAX_EDGE_PX` on the long edge,
 * encode as JPEG or WebP, then close the bitmap.
 */
async function encodeImageBitmapToBytes(
  bitmap: ImageBitmap,
  mimeType: 'image/jpeg' | 'image/webp',
  quality: number,
): Promise<Uint8Array> {
  try {
    const { width: bw, height: bh } = bitmap;
    const { width, height } = scaledDimensions(bw, bh, MEMO_STAGING_MAX_EDGE_PX);
    const canvas = document.createElement('canvas');
    canvas.width = width;
    canvas.height = height;
    const ctx = canvas.getContext('2d');
    if (!ctx) {
      throw new Error('Canvas 2D context unavailable');
    }
    ctx.drawImage(bitmap, 0, 0, width, height);
    const blob = await new Promise<Blob | null>((resolve) =>
      canvas.toBlob(resolve, mimeType, quality),
    );
    if (!blob) {
      throw new Error('canvas.toBlob returned null');
    }
    return new Uint8Array(await blob.arrayBuffer());
  } finally {
    bitmap.close();
  }
}

/**
 * Downscale via an <img> element and canvas. Works where `createImageBitmap` rejects a large
 * source (iOS Safari does this on full-resolution phone photos), which is what used to send the
 * raw file instead.
 */
async function encodeViaImageElement(
  blob: Blob,
  mimeType: 'image/jpeg' | 'image/webp',
  quality: number,
): Promise<Uint8Array> {
  const url = URL.createObjectURL(blob);
  try {
    const img = new Image();
    img.decoding = 'async';
    img.src = url;
    await img.decode();
    const { width, height } = scaledDimensions(
      img.naturalWidth,
      img.naturalHeight,
      MEMO_STAGING_MAX_EDGE_PX,
    );
    const canvas = document.createElement('canvas');
    canvas.width = width;
    canvas.height = height;
    const ctx = canvas.getContext('2d');
    if (!ctx) {
      throw new Error('Canvas 2D context unavailable');
    }
    ctx.drawImage(img, 0, 0, width, height);
    const out = await new Promise<Blob | null>((resolve) =>
      canvas.toBlob(resolve, mimeType, quality),
    );
    if (!out) {
      throw new Error('canvas.toBlob returned null');
    }
    return new Uint8Array(await out.arrayBuffer());
  } finally {
    URL.revokeObjectURL(url);
  }
}

async function rawImageBytesForMemoFallback(file: File): Promise<{
  data: Uint8Array;
  mimeType: string;
  extension: string;
}> {
  const arrayBuffer = await file.arrayBuffer();
  let fileData = new Uint8Array(arrayBuffer);
  let mimeType = getEnhancedMimeType(file.name, fileData);
  let extension = file.name.split('.').pop() || 'jpg';

  if (mimeType === 'image/heic' || mimeType === 'image/heif') {
    try {
      const convertedBlob = await convertHeicToJpeg(fileData, file.name, mimeType);
      const convertedBuffer = await convertedBlob.arrayBuffer();
      fileData = new Uint8Array(convertedBuffer);
      mimeType = 'image/jpeg';
      extension = 'jpg';
    } catch (error) {
      console.error('Client-side HEIC conversion failed, uploading original:', error);
    }
  }

  return { data: fileData, mimeType, extension };
}

/**
 * Resize/compress an image in the browser for memo staging (`testbuckethelios`).
 * An image is never sent at full resolution: if `createImageBitmap` fails, the <img>+canvas path
 * is tried; if that fails too the upload errors. A raw multi-MB photo decodes to hundreds of MB
 * server-side and has OOM-killed the prod container. Non-images pass through untouched.
 * Skips re-encode when already within max edge and under `MEMO_STAGING_SKIP_REENCODE_MAX_BYTES`.
 */
export async function prepareImageFileForMemoStagingUpload(file: File): Promise<{
  data: Uint8Array;
  mimeType: string;
  extension: string;
}> {
  if (!isImageForStaging(file)) {
    return rawImageBytesForMemoFallback(file);
  }
  if (typeof document === 'undefined') {
    throw new Error(`Cannot downscale ${file.name} outside a browser`);
  }

  let decodeInput: Blob = file;
  try {
    const header = new Uint8Array(await file.slice(0, Math.min(file.size, 65536)).arrayBuffer());
    const enhanced = getEnhancedMimeType(file.name, header);

    if (enhanced === 'image/heic' || enhanced === 'image/heif') {
      const full = new Uint8Array(await file.arrayBuffer());
      decodeInput = await convertHeicToJpeg(full, file.name, enhanced);
    }
    if (typeof createImageBitmap !== 'function') {
      throw new Error('createImageBitmap unavailable');
    }

    const wantsWebp = enhanced === 'image/webp' || file.name.toLowerCase().endsWith('.webp');

    const bmp = await createImageBitmap(decodeInput);
    const fitsCanvas =
      bmp.width <= MEMO_STAGING_MAX_EDGE_PX && bmp.height <= MEMO_STAGING_MAX_EDGE_PX;
    const smallEnough = decodeInput.size <= MEMO_STAGING_SKIP_REENCODE_MAX_BYTES;

    if (fitsCanvas && smallEnough) {
      bmp.close();
      const data = new Uint8Array(await decodeInput.arrayBuffer());
      if (wantsWebp) {
        return { data, mimeType: 'image/webp', extension: 'webp' };
      }
      if (enhanced === 'image/png') {
        return { data, mimeType: 'image/png', extension: 'png' };
      }
      return { data, mimeType: 'image/jpeg', extension: 'jpg' };
    }

    if (wantsWebp) {
      try {
        const data = await encodeImageBitmapToBytes(bmp, 'image/webp', MEMO_STAGING_WEBP_QUALITY);
        return { data, mimeType: 'image/webp', extension: 'webp' };
      } catch {
        const bmpJ = await createImageBitmap(decodeInput);
        const data = await encodeImageBitmapToBytes(bmpJ, 'image/jpeg', MEMO_STAGING_JPEG_QUALITY);
        return { data, mimeType: 'image/jpeg', extension: 'jpg' };
      }
    }

    const data = await encodeImageBitmapToBytes(bmp, 'image/jpeg', MEMO_STAGING_JPEG_QUALITY);
    return { data, mimeType: 'image/jpeg', extension: 'jpg' };
  } catch (bitmapErr) {
    try {
      const data = await encodeViaImageElement(
        decodeInput,
        'image/jpeg',
        MEMO_STAGING_JPEG_QUALITY,
      );
      return { data, mimeType: 'image/jpeg', extension: 'jpg' };
    } catch (imgErr) {
      console.error('Image downscale failed; refusing to upload at full resolution', {
        file: file.name,
        bitmapErr,
        imgErr,
      });
      throw new Error(`Could not downscale ${file.name}; it was not uploaded`);
    }
  }
}

export const PREVIEW_THUMB_MAX_EDGE_PX = 160;

/**
 * Small preview thumbnail as an object URL. Decoding the full photo once here and previewing
 * the tiny result keeps a stack of multi-megapixel attachments from dragging the page; the
 * caller revokes the URL when the preview goes away. Falls back to the raw file's URL when
 * canvas/`createImageBitmap` is unavailable.
 */
export async function createImagePreviewThumbnail(file: File): Promise<string> {
  if (typeof document === 'undefined' || typeof createImageBitmap !== 'function') {
    return URL.createObjectURL(file);
  }

  try {
    const header = new Uint8Array(await file.slice(0, Math.min(file.size, 65536)).arrayBuffer());
    const enhanced = getEnhancedMimeType(file.name, header);

    let decodeInput: Blob = file;
    if (enhanced === 'image/heic' || enhanced === 'image/heif') {
      const full = new Uint8Array(await file.arrayBuffer());
      decodeInput = await convertHeicToJpeg(full, file.name, enhanced);
    }

    const bitmap = await createImageBitmap(decodeInput);
    try {
      const { width, height } = scaledDimensions(
        bitmap.width,
        bitmap.height,
        PREVIEW_THUMB_MAX_EDGE_PX,
      );
      const canvas = document.createElement('canvas');
      canvas.width = width;
      canvas.height = height;
      const ctx = canvas.getContext('2d');
      if (!ctx) {
        throw new Error('Canvas 2D context unavailable');
      }
      ctx.drawImage(bitmap, 0, 0, width, height);
      const blob = await new Promise<Blob | null>((resolve) =>
        canvas.toBlob(resolve, 'image/jpeg', 0.7),
      );
      if (!blob) {
        throw new Error('canvas.toBlob returned null');
      }
      return URL.createObjectURL(blob);
    } finally {
      bitmap.close();
    }
  } catch {
    // Full-size fallback still shows something, even if it costs more memory.
    return URL.createObjectURL(file);
  }
}

/** Concurrency for client-side image preparation before memo creation. */
export const MEMO_STAGING_PREPARE_CONCURRENCY = 3;

/**
 * Returns true if `file` looks like a raster image we should downscale via
 * `prepareImageFileForMemoStagingUpload`. PDFs and video files are excluded so
 * they fall back to the raw `fileToFileObject` path.
 */
export const isImageForStaging = (file: File): boolean => {
  const t = (file.type || '').toLowerCase();
  if (t.startsWith('image/')) return true;
  return /\.(jpe?g|png|webp|heic|heif)$/i.test(file.name);
};

/**
 * Convert a `File` into a downscaled, re-encoded `FileObject` for memo staging.
 * Keeps the inline `MemoService.CreateFullMemo` payload small enough to avoid
 * gRPC-web proxy/server size limits when multiple images are sent at once.
 * Pair with `runWithConcurrency(..., MEMO_STAGING_PREPARE_CONCURRENCY, ...)`
 * when preparing multiple files.
 */
export const imageFileToStagedFileObject = async (
  file: File,
  bucket = 'testbuckethelios',
): Promise<FileObject> => {
  const prepared = await prepareImageFileForMemoStagingUpload(file);
  const stem = file.name.replace(/\.[^/.]+$/, '') || 'photo';
  const uniqueName = generateUniqueFileName(`${stem}.${prepared.extension}`);
  return FileObject.create({
    bucket,
    key: uniqueName,
    mimeType: prepared.mimeType,
    data: prepared.data,
  });
};

/**
 * Downscale/re-encode a photo into a `File` for direct S3 upload paths (e.g.
 * reading photos) using the same pipeline as memo staging. Non-images and
 * preparation failures fall back to the original file so an upload is never
 * lost to preparation.
 */
export const compressImageFileForUpload = async (file: File): Promise<File> => {
  if (!isImageForStaging(file)) {
    return file;
  }
  try {
    const prepared = await prepareImageFileForMemoStagingUpload(file);
    const stem = file.name.replace(/\.[^/.]+$/, '') || 'photo';
    return new File([prepared.data], `${stem}.${prepared.extension}`, {
      type: prepared.mimeType,
    });
  } catch {
    return file;
  }
};

/**
 * Run async work on `items` with at most `limit` concurrent executions (order preserved in result).
 */
export async function runWithConcurrency<T, R>(
  items: T[],
  limit: number,
  fn: (item: T, index: number) => Promise<R>,
): Promise<R[]> {
  if (items.length === 0) return [];
  const results = new Array<R>(items.length);
  let nextIndex = 0;
  const workerCount = Math.min(Math.max(1, limit), items.length);

  const worker = async () => {
    for (;;) {
      const i = nextIndex++;
      if (i >= items.length) break;
      results[i] = await fn(items[i], i);
    }
  };

  await Promise.all(Array.from({ length: workerCount }, () => worker()));
  return results;
}

// ---------------------------------------------------------------------------
// File → FileObject conversion
// ---------------------------------------------------------------------------

/**
 * Convert HEIC/HEIF bytes to JPEG before a transaction (or similar) upload.
 * Throws if conversion does not produce a JPEG, so callers do not store raw HEIC.
 */
export const convertHeicUploadBytesIfNeeded = async (
  fileData: Uint8Array,
  fileName: string,
): Promise<{ fileData: Uint8Array; fileName: string; mimeType: string }> => {
  const mimeType = getEnhancedMimeType(fileName, fileData);
  if (mimeType !== 'image/heic' && mimeType !== 'image/heif') {
    return { fileData, fileName, mimeType };
  }

  const convertedBlob = await convertHeicToJpeg(fileData, fileName, mimeType);
  if (convertedBlob.type === 'image/heic' || convertedBlob.type === 'image/heif') {
    throw new Error(
      'Could not convert this HEIC photo. Save it as a JPG on your phone and try again.',
    );
  }

  return {
    fileData: new Uint8Array(await convertedBlob.arrayBuffer()),
    fileName: fileName.replace(/\.[^/.]+$/, '.jpg'),
    mimeType: 'image/jpeg',
  };
};

/**
 * Convert a `File` into a proto `FileObject` with enhanced MIME type detection.
 * HEIC/HEIF images are automatically converted to JPEG client-side before
 * upload so the backend never receives raw HEIC bytes.
 *
 * The full file data is read into memory, so this is suitable for regular-sized
 * files. For large videos, use `fileToFileObjectHeader` instead.
 */
export const fileToFileObject = async (
  file: File,
  bucket = 'testbuckethelios',
): Promise<FileObject> => {
  const arrayBuffer = await file.arrayBuffer();
  // Annotated rather than inferred: `new Uint8Array(arrayBuffer)` narrows to
  // Uint8Array<ArrayBuffer>, which the ArrayBufferLike-backed array coming back from
  // convertHeicUploadBytesIfNeeded can't be assigned to.
  let fileData: Uint8Array = new Uint8Array(arrayBuffer);
  let mimeType = getEnhancedMimeType(file.name, fileData);
  let fileName = file.name;

  // Convert HEIC/HEIF → JPEG on the client so the backend doesn't have to
  try {
    const converted = await convertHeicUploadBytesIfNeeded(fileData, fileName);
    fileData = converted.fileData;
    fileName = converted.fileName;
    mimeType = converted.mimeType;
  } catch (error) {
    console.error('Client-side HEIC conversion failed, uploading original:', error);
  }

  const uniqueName = generateUniqueFileName(fileName);

  return FileObject.create({
    bucket,
    key: uniqueName,
    mimeType,
    data: fileData,
  });
};

/**
 * Build a `FileObject` reference for a large file by reading only the first
 * 512 KB for MIME detection, and leaving `data` empty (the actual upload is
 * handled via the multipart upload flow).
 */
export const fileToFileObjectHeader = async (
  file: File,
  bucket = 'testbuckethelios',
): Promise<{ fileObject: FileObject; uniqueName: string; mimeType: string }> => {
  const headerChunk = new Uint8Array(await file.slice(0, 512 * 1024).arrayBuffer());
  const mimeType = getEnhancedMimeType(file.name, headerChunk);
  const uniqueName = generateUniqueFileName(file.name);

  return {
    fileObject: FileObject.create({
      bucket,
      key: uniqueName,
      mimeType,
      data: new Uint8Array(0),
    }),
    uniqueName,
    mimeType,
  };
};

// ---------------------------------------------------------------------------
// Memo file-ID CSV helpers
// ---------------------------------------------------------------------------

/** Merge new file IDs into an existing CSV string, returning the updated CSV. */
export const mergeFileIdCsv = (existingCsv: string, newIds: number[]): string => {
  const existingArray = existingCsv
    ? existingCsv
        .split(',')
        .map((id) => id.trim())
        .filter(Boolean)
    : [];
  return [...existingArray, ...newIds.map((id) => id.toString())].join(',');
};

// ---------------------------------------------------------------------------
// Audio conversion
// ---------------------------------------------------------------------------

/** Convert an audio blob to WAV format (16-bit PCM). */
export const convertToWav = async (audioBlob: Blob): Promise<Uint8Array> => {
  const audioContext = new (window.AudioContext || (window as any).webkitAudioContext)();
  const arrayBuffer = await audioBlob.arrayBuffer();
  const audioBuffer = await audioContext.decodeAudioData(arrayBuffer);

  const numberOfChannels = audioBuffer.numberOfChannels;
  const length = audioBuffer.length;
  const sampleRate = audioBuffer.sampleRate;

  const bytesPerSample = 2; // 16-bit
  const blockAlign = numberOfChannels * bytesPerSample;
  const byteRate = sampleRate * blockAlign;
  const dataSize = length * blockAlign;
  const bufferSize = 44 + dataSize;

  const buffer = new ArrayBuffer(bufferSize);
  const view = new DataView(buffer);

  const writeString = (offset: number, str: string) => {
    for (let i = 0; i < str.length; i++) {
      view.setUint8(offset + i, str.charCodeAt(i));
    }
  };

  writeString(0, 'RIFF');
  view.setUint32(4, bufferSize - 8, true);
  writeString(8, 'WAVE');
  writeString(12, 'fmt ');
  view.setUint32(16, 16, true);
  view.setUint16(20, 1, true);
  view.setUint16(22, numberOfChannels, true);
  view.setUint32(24, sampleRate, true);
  view.setUint32(28, byteRate, true);
  view.setUint16(32, blockAlign, true);
  view.setUint16(34, 16, true);
  writeString(36, 'data');
  view.setUint32(40, dataSize, true);

  let offset = 44;
  for (let i = 0; i < length; i++) {
    for (let channel = 0; channel < numberOfChannels; channel++) {
      const channelData = audioBuffer.getChannelData(channel);
      const sample = Math.max(-1, Math.min(1, channelData[i]));
      view.setInt16(offset, sample * 0x7fff, true);
      offset += 2;
    }
  }

  return new Uint8Array(buffer);
};

/** Create a WAV `FileObject` from an audio `Blob`. */
export const audioToFileObject = async (
  audioBlob: Blob,
  bucket = 'testbuckethelios',
): Promise<FileObject> => {
  const ts = Date.now();
  const wavData = await convertToWav(audioBlob);

  return FileObject.create({
    bucket,
    key: `audio-note-${ts}.wav`,
    mimeType: 'audio/wav',
    data: wavData,
  });
};
